import { ENDPOINTS } from "../../constant/routes";
import { request } from "../../services/requests";

/** Furthest step an email reached; the last group are problems. */
export type EmailStatus =
  | "sent"
  | "deferred"
  | "delivered"
  | "opened"
  | "clicked"
  | "unsubscribed"
  | "soft_bounce"
  | "spam"
  | "blocked"
  | "invalid"
  | "hard_bounce"
  | "error";

export interface EmailEvent {
  /** Brevo's raw name, e.g. requests, delivered, loadedByProxy */
  event: string;
  status: EmailStatus | string;
  at: string | null;
  reason: string | null;
  link: string | null;
}

export interface SentEmail {
  message_id: string;
  email: string;
  subject: string | null;
  from: string | null;
  template_id: number | null;
  template_name: string | null;
  tags: string[];
  sent_at: string | null;
  status: EmailStatus;
  opened: boolean;
  clicked: boolean;
  events: EmailEvent[];
}

export interface EmailSummary {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  problems: number;
}

export interface SentEmailsResponse {
  start: string;
  end: string;
  /** More events than one request pulls; narrow the range */
  truncated: boolean;
  /** Whole window, before the status and search filters */
  summary: EmailSummary;
  templates: { id: number; name: string }[];
  messages: SentEmail[];
}

export interface SentEmailsParams {
  /** YYYY-MM-DD, at most 90 days apart */
  start?: string;
  end?: string;
  template_id?: number;
  /** sent, delivered, opened, clicked or "problems" */
  status?: string;
  q?: string;
}

export const getSentEmails = (token: string, params: SentEmailsParams = {}) =>
  request(token).get<SentEmailsResponse>(`${ENDPOINTS.EMAILS}/messages`, { params });

export type EmailContent =
  | { available: false }
  | { available: true; subject: string | null; email: string | null; html: string; attachment_count: number };

export const getEmailContent = (token: string, messageId: string) =>
  request(token).get<EmailContent>(`${ENDPOINTS.EMAILS}/messages/content`, { params: { message_id: messageId } });

export interface CampaignStats {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  unsubscribed: number;
  complaints: number;
}

export interface Campaign {
  id: number;
  name: string;
  subject: string | null;
  preview_text: string | null;
  /** draft, sent, queued, suspended, inProcess, archive, ... */
  status: string;
  sent_at: string | null;
  scheduled_at: string | null;
  created_at: string | null;
  from: string | null;
  list_ids: number[];
  share_link: string | null;
  stats: CampaignStats;
}

export interface CampaignDetail extends Campaign {
  html: string | null;
  links: { url: string; clicks: number }[];
}

export const getCampaigns = (token: string) =>
  request(token).get<{ count: number; campaigns: Campaign[] }>(`${ENDPOINTS.EMAILS}/campaigns`, { params: { limit: 100 } });

export const getCampaign = (token: string, id: number) =>
  request(token).get<CampaignDetail>(`${ENDPOINTS.EMAILS}/campaigns/${id}`);
